/**
 * Demo-build Clerk stub (ADR 0006 decision 4 · deploy plan D2).
 *
 * `vite.config.ts` aliases `@clerk/react` to this module ONLY when
 * `VITE_DEMO=true` (`pnpm build:demo`), so the real SDK is never bundled and
 * the demo artifact can produce zero auth network traffic. It reports ONE
 * permanently signed-in Admin whose identity matches the mock `/me` payload
 * (`src/__mocks__/factories/user.ts`: `mock.user@example.com`, owning tenant
 * `bylt`, role Admin) so profile UI, tenant views, and role-gated affordances
 * agree with the MSW-mocked API.
 *
 * CONTRACT: this file must export every VALUE symbol the bundle imports from
 * `@clerk/react` (audited 2026-07-06: ClerkProvider, useAuth, useClerk,
 * useReverification, useSession, useSignIn, useSignUp, useUser). A new Clerk
 * import without a matching export here breaks `build:demo` loudly — that
 * drift alarm is intentional, do not add a catch-all. `@clerk/shared/*`
 * (error classes + types, no network) is NOT aliased and stays real.
 *
 * Hook returns are module-level singletons: the app gates rendering on these
 * objects in effect deps (see `app.tsx`), so referential stability matters.
 * Types are local/loose on purpose — consumers typecheck against the real SDK
 * (tsc never sees the alias); this module only owes runtime shape.
 */

import type { PropsWithChildren, ReactNode } from 'react'

const DEMO_USER_ID = 'user_demo_admin'
/** Must equal the mock `/me` `user.uname` so both identity sources agree. */
const DEMO_EMAIL = 'mock.user@example.com'

/** base64url (no padding) per the JWT spec. */
function b64url(payload: object): string {
	return btoa(JSON.stringify(payload))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '')
}

/**
 * Unsigned JWT with a far-future `exp`: `getTokenWithLeeway` (`lib/auth`)
 * decodes `exp` and force-refreshes near-expired tokens — a distant expiry
 * keeps it on the cached path. The token never reaches a real host (MSW
 * intercepts all API traffic; the API base is an `.invalid` host anyway).
 */
const DEMO_JWT = [
	b64url({ alg: 'none', typ: 'JWT' }),
	b64url({ sub: DEMO_USER_ID, exp: 4102444800 }), // 2100-01-01T00:00:00Z
	'',
].join('.')

const getToken = async (_options?: unknown): Promise<string | null> => DEMO_JWT

/**
 * Every interactive auth attempt (sign-in/sign-up/redirect flows) resolves to
 * a visible error instead of throwing: the Core-3 form code reads
 * `const { error } = await signIn.…()`, so this renders a clear message if an
 * auth route is ever reached in demo rather than crashing the screen.
 */
const authDisabled = async (..._args: unknown[]) => ({
	error: new Error('Authentication is disabled in the demo'),
	status: null,
})

const demoEmailAddress = {
	id: 'idn_demo_email',
	emailAddress: DEMO_EMAIL,
	verification: { status: 'verified' },
}

/**
 * The one signed-in demo Admin. Mutable so `updateMetadata()` can
 * theatrically merge `unsafeMetadata` (widget layout + onboarding sync write
 * through it) — in-memory only, nothing persists (ADR 0006 decision 5).
 *
 * `totpEnabled: true` matters: `seedDashboardSession` redirects non-SSO users
 * without TOTP to `/setup-totp`, which would trap the demo at boot. Empty
 * `externalAccounts`/`enterpriseAccounts` read as a password (non-SSO) user.
 */
const demoUser = {
	id: DEMO_USER_ID,
	firstName: 'Mock',
	lastName: 'User',
	fullName: 'Mock User',
	username: null,
	imageUrl: '',
	hasImage: false,
	primaryEmailAddress: demoEmailAddress,
	emailAddresses: [demoEmailAddress],
	totpEnabled: true,
	twoFactorEnabled: true,
	passwordEnabled: true,
	backupCodeEnabled: false,
	externalAccounts: [] as unknown[],
	enterpriseAccounts: [] as unknown[],
	publicMetadata: {} as Record<string, unknown>,
	unsafeMetadata: {} as Record<string, unknown>,
	createdAt: new Date('2026-01-01T00:00:00.000Z'),
	updatedAt: new Date('2026-01-01T00:00:00.000Z'),
	// Real updateMetadata deep-merges; a shallow merge is enough here because
	// call sites write whole top-level keys (widget state / onboarding).
	async updateMetadata(params: {
		unsafeMetadata?: Record<string, unknown>
	}): Promise<unknown> {
		if (params.unsafeMetadata) {
			demoUser.unsafeMetadata = {
				...demoUser.unsafeMetadata,
				...params.unsafeMetadata,
			}
		}
		return demoUser
	},
	async updatePassword(_params: unknown): Promise<unknown> {
		return demoUser
	},
	async setProfileImage(_params: unknown): Promise<unknown> {
		return { id: 'img_demo' }
	},
	// TOTP management (setup-totp form) — theatrical success values.
	async createTOTP(): Promise<unknown> {
		return { secret: 'DEMODEMODEMODEMO', uri: 'otpauth://totp/AIR%20Demo' }
	},
	async verifyTOTP(_params: unknown): Promise<unknown> {
		return { verified: true }
	},
}

const demoSession = {
	id: 'sess_demo',
	status: 'active',
	user: demoUser,
	lastActiveAt: new Date(),
	getToken,
	// Reverification never triggers in demo (see useReverification below);
	// these exist so the dialog/setup-totp plumbing can't crash if reached.
	startVerification: async (_params: unknown) => ({ status: 'complete' }),
	attemptFirstFactorVerification: async (_params: unknown) => ({
		status: 'complete',
	}),
	attemptSecondFactorVerification: async (_params: unknown) => ({
		status: 'complete',
	}),
}

const demoSignIn = {
	status: null,
	supportedFirstFactors: [] as unknown[],
	firstFactorVerification: null,
	create: authDisabled,
	password: authDisabled,
	sso: authDisabled,
	finalize: authDisabled,
	mfa: {
		verifyTOTP: authDisabled,
		verifyEmailCode: authDisabled,
		sendEmailCode: authDisabled,
	},
}

const demoSignUp = {
	status: null,
	create: authDisabled,
	sso: authDisabled,
	finalize: authDisabled,
}

const setActive = async (_params: unknown): Promise<void> => {}

/** `useClerk()` singleton — `loaded: true` satisfies the app.tsx boot gate. */
const demoClerk = {
	loaded: true,
	user: demoUser,
	session: demoSession,
	signOut: async (_options?: unknown): Promise<void> => {},
	setActive,
	client: {
		signIn: demoSignIn,
		signUp: demoSignUp,
		sessions: [demoSession],
		handleRedirectCallback: authDisabled,
	},
}

const authReturn = {
	isLoaded: true,
	isSignedIn: true,
	userId: DEMO_USER_ID,
	sessionId: demoSession.id,
	orgId: null,
	orgRole: null,
	orgSlug: null,
	has: (_params: unknown) => true,
	getToken,
	signOut: demoClerk.signOut,
}

const userReturn = { isLoaded: true, isSignedIn: true, user: demoUser }

const sessionReturn = { isLoaded: true, isSignedIn: true, session: demoSession }

const signInReturn = {
	isLoaded: true,
	fetchStatus: 'idle',
	signIn: demoSignIn,
	setActive,
}

const signUpReturn = {
	isLoaded: true,
	fetchStatus: 'idle',
	signUp: demoSignUp,
	setActive,
}

/** Renders children directly — no auth context, no network, no gating. */
export function ClerkProvider(
	props: PropsWithChildren<Record<string, unknown>>,
): ReactNode {
	return props.children
}

export function useAuth() {
	return authReturn
}

export function useClerk() {
	return demoClerk
}

export function useUser() {
	return userReturn
}

export function useSession() {
	return sessionReturn
}

export function useSignIn() {
	return signInReturn
}

export function useSignUp() {
	return signUpReturn
}

/**
 * Pass-through: the demo session never needs reverification, so the wrapped
 * action runs directly and `onNeedsReverification` is never invoked.
 */
export function useReverification<Args extends unknown[], Return>(
	action: (...args: Args) => Return,
	_options?: unknown,
): (...args: Args) => Return {
	return action
}
